import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi, User } from '@/lib/api/services/auth';
import { tokenRefreshService } from '../services/TokenRefreshService';
import { syncWhatsNewSeenFromPreferences } from '@/lib/releaseNotes';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /**
   * True once the initial profile fetch after page load has resolved (success
   * or failure). On warm-start with a stored user, `loading` flips false
   * synchronously after hydrating localStorage, but the background
   * `getProfile()` call may still be in flight — during that window, fields
   * like `menu_permissions` can change and cause the sidebar to re-filter
   * (the "few default items then more" stagger team members report).
   * Components that gate UI on a stable user shape should wait on this flag.
   * `true` immediately for unauthenticated mounts (nothing to fetch).
   */
  profileSynced: boolean;
  signOut: () => Promise<void>;
  login: (email: string, password: string, adminLogin?: boolean) => Promise<{ success: boolean; error?: string; requires_totp?: boolean; userId?: string; user?: User }>;
  loginWithOtp: (phoneNumber: string, otpCode: string, adminLogin?: boolean) => Promise<{ success: boolean; error?: string; requires_totp?: boolean; userId?: string; user?: User }>;
  verifyLoginTotp: (userId: string, token: string) => Promise<{ success: boolean; error?: string }>;
  signup: (name: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signupWithPhone: (fullName: string, phoneNumber: string, otpCode: string, referralCode?: string) => Promise<{ success: boolean; error?: string; user?: User }>;
  updateUser: (userData: User) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Extract menu_permissions from preferences so sidebar can access it directly
function normalizeUser(userData: User | null): User | null {
  if (!userData) return null;
  let prefs = userData.preferences;
  if (typeof prefs === 'string') {
    try { prefs = JSON.parse(prefs); } catch { prefs = null; }
  }
  const menuPermissions = (prefs as Record<string, unknown> | null)?.menu_permissions ??
    (userData as unknown as Record<string, unknown>).menu_permissions ??
    undefined;
  const normalized = { ...userData, preferences: prefs ?? userData.preferences, menu_permissions: menuPermissions } as unknown as User;
  if (normalized.id) {
    syncWhatsNewSeenFromPreferences(normalized.id, normalized.preferences);
  }
  return normalized;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileSynced, setProfileSynced] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');

      if (!token) {
        // No session to sync — gates can release immediately.
        setProfileSynced(true);
      }

      if (token) {
        try {
          // Check if we should logout due to token issues
          if (tokenRefreshService.shouldLogout()) {
            tokenRefreshService.clearTokens();
            setUser(null);
            setLoading(false);
            setProfileSynced(true);
            return;
          }

          // First, try to restore user from localStorage
          if (storedUser) {
            const userData = JSON.parse(storedUser);

            // Validate that userData has required fields. Email is NO LONGER
            // required — phone-first accounts have a null email; gating on
            // email here would wrongly evict them on every reload.
            if (userData && userData.id) {
              setUser(normalizeUser(userData));

              // Set up automatic token refresh
              tokenRefreshService.setupAutoRefresh();

              // Then try to validate with backend (but don't fail if backend is down)
              try {
                const result = await authApi.getProfile();
                if (result.data) {
                  // Update user data if API call succeeds
                  const normalized = normalizeUser(result.data);
                  setUser(normalized);
                  localStorage.setItem('user', JSON.stringify(normalized));
                }
              } catch (apiError) {
                // Keep using cached user data if backend is unavailable
              }
            } else {
              // Invalid user data, clear everything
              tokenRefreshService.clearTokens();
              setUser(null);
            }
          } else {
            // No stored user data, try to get from backend
            try {
              const result = await authApi.getProfile();
              if (result.data) {
                const normalized = normalizeUser(result.data);
                setUser(normalized);
                localStorage.setItem('user', JSON.stringify(normalized));
                // Set up automatic token refresh
                tokenRefreshService.setupAutoRefresh();
              } else {
                // No valid user data from backend either
                tokenRefreshService.clearTokens();
                setUser(null);
              }
            } catch (apiError) {
              tokenRefreshService.clearTokens();
              setUser(null);
            }
          }
        } catch (error) {
          // Only clear tokens if there's a real error (not just network issues)
          tokenRefreshService.clearTokens();
          setUser(null);
        }
      }
      setLoading(false);
      // Mark the initial-mount profile sync as complete regardless of outcome.
      // Gates downstream (ProtectedRoute, AppSidebar) release here so any field
      // refreshed from the backend (notably menu_permissions for team members)
      // has landed before the real UI paints.
      setProfileSynced(true);
    };

    checkAuth();
  }, []);

  // Re-fetch profile when tab becomes visible or window is focused.
  // This ensures permissions updated by an admin are applied without requiring re-login.
  useEffect(() => {
    const refreshProfile = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      try {
        // Ensure token is valid before making API call (refresh if needed)
        const tokenValid = await tokenRefreshService.ensureValidToken();
        if (!tokenValid) {
          // Token expired and refresh failed — don't logout immediately,
          // let the API client handle it on next request
          return;
        }
        const result = await authApi.getProfile();
        if (result.data) {
          const normalized = normalizeUser(result.data);
          setUser(normalized);
          localStorage.setItem('user', JSON.stringify(normalized));
        }
      } catch {
        // Silently ignore — keep existing user data if backend is unreachable
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) refreshProfile();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', refreshProfile);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', refreshProfile);
    };
  }, []);

  const login = async (email: string, password: string, adminLogin: boolean = false) => {
    try {
      setLoading(true);
      const result = await authApi.login({ email, password, adminLogin });

      if (result.data) {
        if (result.data.requires_totp) {
          return { success: true, requires_totp: true, userId: result.data.userId };
        }

        // Store tokens using the token refresh service
        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        // Login response is the authoritative user — no follow-up getProfile
        // needed before the UI can settle.
        setProfileSynced(true);

        // Set up automatic token refresh
        tokenRefreshService.setupAutoRefresh();

        return { success: true, user: normalizedUser! };
      } else {
        return { success: false, error: result.error || 'Login failed' };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'Login failed';
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const loginWithOtp = async (phoneNumber: string, otpCode: string, adminLogin: boolean = false) => {
    try {
      setLoading(true);
      const result = await authApi.loginWithOtp(phoneNumber, otpCode, adminLogin);

      if (result.data) {
        if (result.data.requires_totp) {
          return { success: true, requires_totp: true, userId: result.data.userId };
        }

        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        setProfileSynced(true);
        tokenRefreshService.setupAutoRefresh();

        return { success: true, user: normalizedUser! };
      } else {
        return { success: false, error: result.error || 'Login failed' };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'Login failed';
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      const result = await authApi.signup({ full_name: name, email, password });

      if (result.data) {
        // Store tokens using the token refresh service
        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedSignupUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedSignupUser));
        setUser(normalizedSignupUser);
        setProfileSynced(true);

        // Set up automatic token refresh
        tokenRefreshService.setupAutoRefresh();

        return { success: true };
      } else {
        return { success: false, error: result.error || 'Signup failed' };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'Signup failed';
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const signupWithPhone = async (fullName: string, phoneNumber: string, otpCode: string, referralCode?: string) => {
    try {
      setLoading(true);
      const result = await authApi.signupWithPhone({
        full_name: fullName,
        phoneNumber,
        otpCode,
        agency_referral_code: referralCode || undefined,
      });

      if (result.data) {
        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        setProfileSynced(true);
        tokenRefreshService.setupAutoRefresh();

        return { success: true, user: normalizedUser! };
      } else {
        return { success: false, error: result.error || 'Signup failed' };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'Signup failed';
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginTotp = async (userId: string, token: string) => {
    try {
      setLoading(true);
      const result = await authApi.verifyLoginTotp(userId, token);

      if (result.data && result.data.token && result.data.user) {
        // Store tokens using the token refresh service
        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedTotpUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedTotpUser));
        setUser(normalizedTotpUser);
        setProfileSynced(true);

        // Set up automatic token refresh
        tokenRefreshService.setupAutoRefresh();

        return { success: true };
      } else {
        return { success: false, error: result.error || 'Verification failed' };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'Verification failed';
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData: User) => {
    const normalized = normalizeUser(userData)!;
    setUser(normalized);
    localStorage.setItem('user', JSON.stringify(normalized));
  };

  const signOut = async () => {
    try {

      // Call logout API
      await authApi.logout();

      // Clear tokens using the token refresh service
      tokenRefreshService.clearTokens();
      setUser(null);

      // Force page reload to auth page
      window.location.href = '/auth';
    } catch (error) {
      // Still clear tokens and redirect even if sign out fails
      tokenRefreshService.clearTokens();
      setUser(null);
      window.location.href = '/auth';
    }
  };

  const value = {
    user,
    loading,
    profileSynced,
    signOut,
    login,
    loginWithOtp,
    verifyLoginTotp,
    signup,
    signupWithPhone,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}